import React, {useEffect, useState} from "react";
import {
    Box,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    IconButton,
    PopoverCloseButton,
    Text,
} from "@chakra-ui/react";

import {useDispatch} from "react-redux";
import {FiDownload, FiShoppingBag} from "react-icons/all";
import {Loader} from "../../components/Loader/Loader";
import {ListItemConfig, ListItemRow} from "../../components/List/ListItem";
import IconWithTooltip from "../../components/Icon/IconWithTooltip";
import {FixedSizeList as List, ListChildComponentProps} from "react-window";
import {UserController} from "../../controllers/UserController";
import {FilterRequest, User} from "../../shared/entities/Users/User";
import {DownloadButton} from "../../components/Buttons/DownloadButton";
import {Company} from "../../shared/entities/Company/Company";

interface ExcludeSubscribePopoverProps {
    item: any;
    onSuccess?: () => void;
    /** {@code summaryOnly=true} — отдельный Excel «продукт → число заказов». */
    clickAction: (vendor: boolean, userId?: number, summaryOnly?: boolean) => Promise<any>;
    /** modal: только окно, открытие через isOpen / onClose (без иконки-триггера) */
    variant?: "popover" | "modal";
    isOpen?: boolean;
    onClose?: () => void;
}

interface CompaniesResponse {
    companies: Company[];
    count: number;
}

const ROW_H_POPOVER = 40;
const ROW_H_MODAL = 46;
const listHeightModal = (rowCount: number) => Math.min(420, Math.max(160, rowCount * ROW_H_MODAL));

const LoadStatementVendorPopover: React.FC<ExcludeSubscribePopoverProps> = ({
    clickAction,
    variant = "popover",
    isOpen: isOpenProp,
    onClose: onCloseProp,
}) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const userController = new UserController(dispatch);
    const [listAll, setListAll] = useState<Array<User>>([]);

    const loadVendors = () => {
        const filterRequest: FilterRequest = {
            user: {name: "", email: ""},
            dealer: {name: "", email: ""},
            company: {name: "Administrators", email: ""},
        };
        setIsLoading(true);
        userController.getCompanyAll({page: 0, size: 100}, filterRequest).then((res) => {
            setIsLoading(false);
            if (res.Some) {
                if ((res.Some as CompaniesResponse).companies[0]) {
                    const filteredUsers = (res.Some as CompaniesResponse).companies[0].users.filter((u) =>
                        u.role.permissions.some((p) => p.actions.includes("select") && p.group === "Dealership_Exclude"),
                    );
                    setListAll(filteredUsers);
                }
            }
        });
    };

    useEffect(() => {
        if (variant === "modal" && isOpenProp) {
            loadVendors();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- load on modal open only
    }, [variant, isOpenProp]);

    const renderPopoverRow = ({index, style}: ListChildComponentProps) => {
        const elem = listAll[index];

        if (!elem) return null;
        const ItemConfig: Array<ListItemConfig> = [
            {
                content: elem.id,
                isLink: false,
                style: {
                    flexBasis: "15%",
                    justifyContent: "flex-start",
                },
            },
            {
                content: elem.login,
                isLink: false,
                style: {
                    flexBasis: "58%",
                    justifyContent: "flex-start",
                },
            },
            {
                content: (
                    <Flex gap={1} justifyContent="flex-end" flexWrap="wrap">
                        <DownloadButton
                            clickAction={() => clickAction(true, Number(elem.id))}
                            label={"Vendor statement (detail)"}
                            icon={FiDownload}
                            nameFile={"vendor-statement.xlsx"}
                        />
                        <DownloadButton
                            clickAction={() => clickAction(true, Number(elem.id), true)}
                            label={"Product order count (summary)"}
                            icon={FiDownload}
                            nameFile={"vendor-product-order-count.xlsx"}
                        />
                    </Flex>
                ),
                isLink: true,
                style: {
                    flexBasis: "22%",
                },
            },
        ];

        return (
            <Box style={style} key={index}>
                <ListItemRow list={ItemConfig}/>
            </Box>
        );
    };

    const renderModalRow = ({index, style}: ListChildComponentProps) => {
        const elem = listAll[index];
        if (!elem) return null;
        return (
            <Flex
                style={style}
                align="center"
                px={4}
                gap={3}
                borderBottom="1px solid"
                borderColor="gray.100"
                bg="white"
                _hover={{bg: "blue.50"}}
                transition="background 0.15s ease"
            >
                <Text w="72px" flexShrink={0} fontSize="xs" fontWeight="600" color="gray.800" fontFamily="mono">
                    {elem.id}
                </Text>
                <Text flex="1" minW={0} fontSize="xs" color="gray.700" noOfLines={1}>
                    {elem.login}
                </Text>
                <Flex flexShrink={0} gap={1} align="center">
                    <DownloadButton
                        clickAction={() => clickAction(true, Number(elem.id))}
                        label={"Vendor statement (detail)"}
                        icon={FiDownload}
                        nameFile={"vendor-statement.xlsx"}
                    />
                    <DownloadButton
                        clickAction={() => clickAction(true, Number(elem.id), true)}
                        label={"Product order count (summary)"}
                        icon={FiDownload}
                        nameFile={"vendor-product-order-count.xlsx"}
                    />
                </Flex>
            </Flex>
        );
    };

    const modalEmpty = !isLoading && listAll.length === 0;

    const modalListSection = (
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden" bg="gray.50">
            <Flex
                px={4}
                py={2}
                bg="linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)"
                borderBottom="1px solid"
                borderColor="gray.200"
                fontSize="10px"
                fontWeight="700"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.05em"
                align="center"
                gap={3}
            >
                <Box w="72px" flexShrink={0}>
                    ID
                </Box>
                <Box flex="1">
                    Login
                </Box>
                <Box minW="88px" flexShrink={0} textAlign="right">
                    Downloads
                </Box>
            </Flex>
            {modalEmpty ? (
                <Flex direction="column" align="center" justify="center" py={10} px={6} bg="white">
                    <Box color="gray.400" mb={2}>
                        <FiShoppingBag size={28} strokeWidth={1}/>
                    </Box>
                    <Text fontSize="xs" fontWeight="medium" color="gray.700" textAlign="center">
                        No suppliers found
                    </Text>
                    <Text fontSize="11px" color="gray.500" textAlign="center" mt={1} maxW="260px" lineHeight="1.4">
                        Users with the required permission will appear here.
                    </Text>
                </Flex>
            ) : (
                <Box bg="white">
                    <List
                        height={listHeightModal(listAll.length)}
                        itemCount={listAll.length}
                        itemSize={ROW_H_MODAL}
                        width="100%"
                    >
                        {(props) => renderModalRow(props)}
                    </List>
                </Box>
            )}
        </Box>
    );

    const popoverListBody = (
        <Box position="relative">
            <Box pl={3}>
                {isLoading ? (
                    <Loader/>
                ) : (
                    <List
                        height={100}
                        itemCount={listAll.length}
                        itemSize={ROW_H_POPOVER}
                        width="100%"
                    >
                        {(props) => renderPopoverRow(props)}
                    </List>
                )}
            </Box>
        </Box>
    );

    if (variant === "modal") {
        return (
            <Modal
                isOpen={!!isOpenProp}
                onClose={() => onCloseProp?.()}
                size="xl"
                scrollBehavior="inside"
                motionPreset="slideInBottom"
            >
                <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)"/>
                <ModalContent
                    maxW="min(640px, 96vw)"
                    maxH="85vh"
                    borderRadius="xl"
                    overflow="hidden"
                    boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.22)"
                    borderWidth="1px"
                    borderColor="gray.100"
                >
                    <ModalHeader
                        pt={5}
                        pb={3}
                        px={5}
                        bg="linear-gradient(135deg, #f8fafc 0%, #eff6ff 55%, #f8fafc 100%)"
                        borderBottom="1px solid"
                        borderColor="gray.100"
                    >
                        <Flex align="flex-start" gap={5} pr={9}>
                            <Flex
                                align="center"
                                justify="center"
                                w="40px"
                                h="40px"
                                borderRadius="lg"
                                bg="white"
                                boxShadow="sm"
                                borderWidth="1px"
                                borderColor="blue.100"
                                color="blue.600"
                                flexShrink={0}
                            >
                                <FiShoppingBag size={18}/>
                            </Flex>
                            <Box minW={0} pl={2}>
                                <Text fontSize="sm" fontWeight="700" color="gray.800" letterSpacing="-0.02em" lineHeight="1.25">
                                    Export of sales
                                </Text>
                                <Text fontSize="11px" color="gray.600" mt={0.5} lineHeight="1.4">
                                    Select a supplier: full statement (collapsible rows) or a separate summary file — product → number of orders.
                                </Text>
                            </Box>
                        </Flex>
                        <ModalCloseButton
                            top={3}
                            right={3}
                            size="sm"
                            borderRadius="md"
                            _hover={{bg: "blackAlpha.100"}}
                        />
                    </ModalHeader>
                    <ModalBody px={5} pt={3} pb={5} fontSize="sm">
                        {isLoading ? (
                            <Flex minH="200px" align="center" justify="center" borderRadius="lg" bg="gray.50" borderWidth="1px" borderColor="gray.100">
                                <Loader/>
                            </Flex>
                        ) : (
                            modalListSection
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <Popover closeOnBlur={true} onOpen={loadVendors}>
            <PopoverTrigger>
                <IconButton bg={"transparent"} _hover={{bg: "transparent"}} _active={{bg: "transparent"}}
                            aria-label="Load Vendor Statement" size={"xs"}>
                    <IconWithTooltip icon={FiShoppingBag} tooltipMessage={'Export of sales data by supplier'} fontSize={"22"}/>
                </IconButton>
            </PopoverTrigger>

            <PopoverContent
                style={{
                    padding: 8,
                    maxHeight: "calc(67vh - 20px)",
                    overflowY: "auto",
                    border: "1.5px solid #6B7280",
                    boxShadow: "0px 4px 32px #B3B2B2",
                    minWidth: "50%",
                }}
            >
                <PopoverCloseButton zIndex="2" ml="auto" position="sticky"/>
                <PopoverBody p={0}>
                    <Box position="relative">
                        {isLoading ? (
                            <Loader/>
                        ) : (
                            popoverListBody
                        )}
                    </Box>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

export default LoadStatementVendorPopover;
